<script setup>
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import Icon from '@/Components/UI/Icon.vue'

defineOptions({ layout: AppLayout })

const { t, tm, rt } = useI18n()

defineProps({
  sections: { type: Array, default: () => [] }
})

// vue-i18n's te() doesn't report true for array-valued keys, so resolve the
// array directly and guard on its length.
function list(key, field) {
  const value = tm(`guide.sections.${key}.${field}`)
  return Array.isArray(value) ? value : []
}
</script>

<template>
  <PageShell :title="t('guide.title')">
    <div class="guide">
      <div class="guide__head">
        <p class="guide__intro">{{ t('guide.intro') }}</p>
        <a
          href="/guide/download"
          target="_blank"
          rel="noopener"
          class="guide__dl"
        >
          <Icon name="download" :size="16" />
          {{ t('guide.download') }}
        </a>
      </div>

      <section
        v-for="key in sections"
        :key="key"
        class="guide__section"
      >
        <h2 class="guide__title">{{ t(`guide.sections.${key}.title`) }}</h2>
        <p
          v-for="(para, i) in list(key, 'paragraphs')"
          :key="i"
          class="guide__p"
        >
          {{ rt(para) }}
        </p>
        <ol
          v-if="list(key, 'steps').length"
          class="guide__steps"
        >
          <li
            v-for="(step, i) in list(key, 'steps')"
            :key="i"
          >
            {{ rt(step) }}
          </li>
        </ol>
      </section>
    </div>
  </PageShell>
</template>

<style scoped>
.guide { max-width: 46rem; }
.guide__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1.5rem; }
.guide__intro { color: var(--inkSoft); font-size: 0.9375rem; line-height: 1.55; margin: 0; flex: 1; }
.guide__dl { display: inline-flex; align-items: center; gap: 0.4rem; flex-shrink: 0; height: 2.25rem; padding: 0 0.75rem; font-size: 0.875rem; font-weight: 500; text-decoration: none; border-radius: var(--radius-button); color: var(--ink); background: var(--surface); border: 1px solid var(--line); transition: background 150ms ease; }
.guide__dl:hover { background: var(--surfaceAlt); }
.guide__section { padding: 1.1rem 0; border-top: 1px solid var(--line); }
.guide__section:first-of-type { border-top: 0; }
.guide__title { font-size: 1.0625rem; font-weight: 700; color: var(--ink); margin: 0 0 0.5rem; }
.guide__p { color: var(--inkSoft); font-size: 0.9375rem; line-height: 1.6; margin: 0 0 0.6rem; }
.guide__steps { margin: 0.25rem 0 0; padding-left: 1.25rem; display: grid; gap: 0.35rem; color: var(--inkSoft); font-size: 0.9375rem; line-height: 1.5; }
</style>
